/**
 * The patient's microdose dose (workbook v1.2, tab 10A and 11_Patient_Why).
 *
 *   DoseStepNotice, on the dashboard:
 *     - a dose change waiting for the patient (WHY-STEP-01 after an approved step, WHY-STEP-02 after a step down) with
 *       mg, mL and units beside the concentration of their vial, a syringe marked at the line, and "I understand my
 *       new dose". Only then does the new dose reach their schedule (OPT-STEP-005);
 *     - a new vial at a different strength (VIAL-01): the same dose, new units, confirmed the same way (OPT-STEP-006);
 *     - otherwise the dose they take now, with the units for their vial, and WHY-STAY-01 when the provider chose Stay;
 *     - before the prescriber signs a microdose plan, WHY-HOLD-01 and nothing else: no drug, dose, score or route
 *       (OPT-TRIG-013, OPT-TC-014).
 *   PortalDoseSteps, the dose page (/portal/dose): the same, with the range the plan allows and the changes so far.
 *   MicrodoseRangeNote, under the microdose item on the plan page: the plan text (WHY-TIRZ-01 / WHY-SEMA-01, with the
 *     off-label line), the dose now, the range the signed ladder allows (OPT-STEP-001) and the changes so far.
 *   MicrodoseDoseLabel: "Starting dose" for the signed item's dose on the plan page, since the dose can change.
 *
 * Everything here is what the clinician prescribed, in plain words. The words come from the API
 * (lib/microdose/patient-text.ts), so the clinic card and the portal say the same thing.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { portalRequest } from '../../lib/portal';
import { MicrodoseSyringe } from '../../components/MicrodoseSyringe';
import { Card, CardHead, DISPLAY, DISPLAY_MID, IconTile, PageTitle, PortalEmpty, PortalError, PortalLoading, TONE, ViewLink, formatPortalDate, usePortalQuery } from './shell';
import { ArrowRight, Calendar, ChatBubble, Syringe, Users } from './icons';

/* ------------------------------------------------------------------ *
 * What the API sends (GET /portal/dose-steps)
 * ------------------------------------------------------------------ */

interface PatientText {
  template: string;
  headline: string;
  body: string;
}

interface PatientDose {
  mgText: string;
  concentrationMgPerMl: string;
  units: string;
  unitsValue: number;
  volumeMl: string;
  scaleUnits: number;
}

interface DoseNotice extends PatientText {
  eventId: string;
  kind: 'approved' | 'step_down';
  startDate: string;
  dose: PatientDose;
}

interface VialNotice extends PatientText {
  eventId: string;
  kind: 'vial_change';
  dose: PatientDose;
}

export interface DoseStepsView {
  hold: PatientText | null;
  ladder: {
    planId: string;
    itemName: string;
    drugName: string;
    sentence: string;
    active: boolean;
    plan: PatientText;
    startText: string;
    current: { mgText: string; since: string; dose: PatientDose | null } | null;
    notice: DoseNotice | null;
    vialNotice: VialNotice | null;
    stay: PatientText | null;
    history: Array<{ at: string; kind: 'raised' | 'lowered'; fromText: string | null; toText: string | null }>;
  } | null;
}

function useDoseSteps() {
  return usePortalQuery<DoseStepsView>('dose-steps', '/dose-steps');
}

/** "Friday, October 23", as the notice text writes it. */
function longDate(value: string): string {
  return new Date(value).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

/** "Since Oct 14" for a dose already on the schedule; "Starting Thursday, October 22" for one that starts later. */
function sinceText(since: string): string {
  return new Date(since).getTime() > Date.now() ? `Starting ${longDate(since)}` : `Since ${formatPortalDate(since)}`;
}

/* ------------------------------------------------------------------ *
 * Pieces
 * ------------------------------------------------------------------ */

function DoseStat({ value, caption, divider = false }: { value: string; caption: string; divider?: boolean }) {
  return (
    <div className={`min-w-0 px-[6px] py-[9px] text-center ${divider ? 'border-l' : ''}`} style={{ borderColor: TONE.line }}>
      <p className="whitespace-nowrap font-display text-[18px] font-semibold leading-none" style={{ ...DISPLAY, color: TONE.heading }}>
        {value}
      </p>
      <p className="mt-[5px] text-[10.5px] leading-[1.3]" style={{ color: TONE.faint }}>
        {caption}
      </p>
    </div>
  );
}

/** mg, units and mL in one row, the units always beside the vial's concentration (OPT-STEP-006). */
function DoseRow({ dose }: { dose: PatientDose }) {
  return (
    <div className="grid grid-cols-3 rounded-[8px]" style={{ background: TONE.row }} data-testid="dose-row">
      <DoseStat value={`${dose.mgText} mg`} caption="once a week" />
      <DoseStat value={`${dose.units} units`} caption="on your syringe" divider />
      <DoseStat value={`${dose.volumeMl} mL`} caption={`with your ${dose.concentrationMgPerMl} mg/mL vial`} divider />
    </div>
  );
}

function SyringeBox({ dose }: { dose: PatientDose }) {
  return (
    <div className="rounded-[8px] border px-[8px] pb-[4px] pt-[6px]" style={{ borderColor: TONE.line }}>
      <MicrodoseSyringe units={dose.unitsValue} scaleUnits={dose.scaleUnits} concentration={dose.concentrationMgPerMl} />
    </div>
  );
}

function Headline({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-display text-[20px] font-semibold leading-[1.15]" style={{ ...DISPLAY_MID, color: TONE.heading }}>
      {children}
    </h2>
  );
}

function Body({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <p className={`text-[13px] leading-relaxed ${className}`} style={{ color: TONE.body }}>
      {children}
    </p>
  );
}

function Eyebrow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-[10px]">
      <IconTile size={32}>{icon}</IconTile>
      <p className="text-[11.5px] font-semibold uppercase leading-none tracking-[0.08em]" style={{ color: TONE.forestText }}>
        {children}
      </p>
    </div>
  );
}

/** WHY-HOLD-01: the neutral review message, before the prescriber decides. */
function HoldCard({ text }: { text: PatientText }) {
  return (
    <Card className="px-[14px] py-[13px]" data-testid="dose-hold">
      <div className="flex gap-[12px]">
        <IconTile size={34} round>
          <Users size={18} strokeWidth={1.7} />
        </IconTile>
        <div className="min-w-0">
          <Headline>{text.headline}</Headline>
          <Body className="mt-[5px]">{text.body}</Body>
        </div>
      </div>
    </Card>
  );
}

function MessageLink() {
  return (
    <Link to="/portal/messages/new" className="mt-[9px] flex items-center justify-center gap-[6px] text-[12.5px] font-medium hover:underline" style={{ color: TONE.forestText }}>
      <ChatBubble size={14} strokeWidth={1.8} />
      Questions? Message your care team
    </Link>
  );
}

/**
 * WHY-STEP-01 / WHY-STEP-02 (a dose change) or VIAL-01 (a new vial), with the syringe, and the patient's "I understand".
 */
function ChangeCard({ notice, onConfirmed }: { notice: DoseNotice | VialNotice; onConfirmed: () => void }) {
  const client = useQueryClient();
  const confirm = useMutation({
    mutationFn: () => portalRequest<{ effectiveFrom: string; newDoseText: string }>(`/dose-steps/${notice.eventId}/acknowledge`, { method: 'POST', body: {} }),
    onSuccess: () => {
      onConfirmed();
      void client.invalidateQueries({ queryKey: ['portal'] });
    },
    onError: () => void client.invalidateQueries({ queryKey: ['portal', 'dose-steps'] }),
  });
  const vial = notice.kind === 'vial_change';
  return (
    <Card className="px-[14px] pb-[14px] pt-[13px]" style={{ borderColor: 'var(--mint)' }} data-testid={vial ? 'vial-notice' : 'dose-notice'}>
      <Eyebrow icon={<Syringe size={18} strokeWidth={1.9} />}>{vial ? 'Your vial' : 'Your dose'}</Eyebrow>
      <div className="mt-[11px]">
        <Headline>{notice.headline}</Headline>
      </div>
      <Body className="mt-[6px]">{notice.body}</Body>
      <div className="mt-[12px]">
        <DoseRow dose={notice.dose} />
      </div>
      <div className="mt-[10px]">
        <SyringeBox dose={notice.dose} />
      </div>
      {!vial && (
        <p className="mt-[9px] flex items-center gap-[7px] text-[12.5px]" style={{ color: TONE.body }}>
          <Calendar size={15} strokeWidth={1.8} />
          Starting {longDate((notice as DoseNotice).startDate)}
        </p>
      )}
      {confirm.error && (
        <p className="mt-[8px] text-[12.5px] text-alert" role="alert">
          {confirm.error instanceof Error ? confirm.error.message : 'Could not save. Try again.'}
        </p>
      )}
      <button
        type="button"
        onClick={() => confirm.mutate()}
        disabled={confirm.isPending}
        className="mt-[12px] flex h-[44px] w-full items-center justify-center rounded-[8px] bg-forest text-[14px] font-semibold text-white transition-colors hover:bg-forest-deep disabled:opacity-60"
        data-testid="dose-acknowledge"
      >
        {confirm.isPending ? 'Saving' : vial ? 'I understand how much to draw' : 'I understand my new dose'}
      </button>
      <MessageLink />
    </Card>
  );
}

/** The dose on the patient's schedule now, with the amount to draw for their vial. */
function CurrentDose({ current, compact = false }: { current: NonNullable<NonNullable<DoseStepsView['ladder']>['current']>; compact?: boolean }) {
  return (
    <>
      <p className="font-display text-[24px] font-semibold leading-none" style={{ ...DISPLAY, color: TONE.heading }}>
        {current.mgText} mg <span className="font-sans text-[13px] font-normal" style={{ color: TONE.body }}>once a week</span>
      </p>
      <p className="mt-[5px] text-[12px]" style={{ color: TONE.faint }}>
        {sinceText(current.since)}
      </p>
      {current.dose ? (
        <>
          <div className="mt-[10px]">
            <DoseRow dose={current.dose} />
          </div>
          {!compact && (
            <div className="mt-[10px]">
              <SyringeBox dose={current.dose} />
            </div>
          )}
        </>
      ) : (
        <Body className="mt-[8px]">The amount to draw appears here once your vial is recorded.</Body>
      )}
    </>
  );
}

/** On the dashboard once nothing waits for the patient: the dose they take now, and why it stayed when it did. */
function YourDoseCard({ ladder, confirmed }: { ladder: NonNullable<DoseStepsView['ladder']>; confirmed: boolean }) {
  if (!ladder.current) return null;
  return (
    <Card className="px-[14px] pb-[12px] pt-[12px]" data-testid="dose-current">
      <div className="flex items-center justify-between gap-3">
        <Eyebrow icon={<Syringe size={18} strokeWidth={1.9} />}>Your dose</Eyebrow>
        {confirmed && (
          <span className="inline-flex h-[21px] items-center gap-[6px] rounded-full pl-[8px] pr-[10px] text-[11px] font-medium leading-none" style={{ background: '#e7f1ec', color: TONE.forestText }} role="status">
            <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-forest" />
            Confirmed
          </span>
        )}
      </div>
      <div className="mt-[12px]">
        <CurrentDose current={ladder.current} compact />
      </div>
      {ladder.stay && (
        <div className="mt-[11px] border-t pt-[10px]" style={{ borderColor: TONE.line }} data-testid="dose-stay">
          <p className="text-[13.5px] font-semibold" style={{ color: TONE.heading }}>
            {ladder.stay.headline}
          </p>
          <Body className="mt-[3px]">{ladder.stay.body}</Body>
        </div>
      )}
      <div className="mt-[10px] flex justify-end">
        <ViewLink to="/portal/dose">Your dose and changes</ViewLink>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * On the dashboard
 * ------------------------------------------------------------------ */

/**
 * The dose card on the dashboard. Shows nothing for a patient without a microdose plan, and nothing while that is
 * being looked up, so every other patient's dashboard is unchanged.
 */
export function DoseStepNotice() {
  const { data, isLoading, error, refetch } = useDoseSteps();
  const [confirmed, setConfirmed] = useState(false);
  if (isLoading) return null;
  if (error) {
    return (
      <Card className="flex items-center justify-between gap-3 px-[14px] py-[11px]">
        <p className="text-[12.5px]" style={{ color: TONE.body }}>
          We could not load your dose.
        </p>
        <button type="button" onClick={() => void refetch()} className="shrink-0 text-[12.5px] font-semibold" style={{ color: TONE.forestText }}>
          Try again
        </button>
      </Card>
    );
  }
  if (data?.hold) return <HoldCard text={data.hold} />;
  const ladder = data?.ladder;
  if (!ladder) return null;
  const waiting = ladder.notice ?? ladder.vialNotice;
  if (waiting) return <ChangeCard key={waiting.eventId} notice={waiting} onConfirmed={() => setConfirmed(true)} />;
  if (!ladder.active) return null;
  return <YourDoseCard ladder={ladder} confirmed={confirmed} />;
}

/* ------------------------------------------------------------------ *
 * The dose page
 * ------------------------------------------------------------------ */

function HistoryList({ history }: { history: NonNullable<DoseStepsView['ladder']>['history'] }) {
  return (
    <ul className="mt-[6px] divide-y" style={{ borderColor: TONE.line }} data-testid="dose-history">
      {history.map((h) => (
        <li key={`${h.at}${h.toText}`} className="flex items-center justify-between gap-3 py-[8px] text-[13px]" style={{ borderColor: TONE.line }}>
          <span style={{ color: TONE.heading }}>
            {h.kind === 'lowered' ? 'Lowered' : 'Raised'} from {h.fromText} to {h.toText} mg
          </span>
          <span className="shrink-0 text-[12px]" style={{ color: TONE.faint }}>
            {formatPortalDate(h.at)}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function PortalDoseSteps() {
  const { data, isLoading, error, refetch } = useDoseSteps();
  const [confirmed, setConfirmed] = useState(false);

  if (isLoading) return <PortalLoading />;
  if (error) {
    return <PortalError message={error instanceof Error ? error.message : 'Could not load your dose'} retry={() => void refetch()} />;
  }

  if (data?.hold) {
    return (
      <div className="space-y-[8px] pb-1">
        <PageTitle title="Your dose" />
        <HoldCard text={data.hold} />
      </div>
    );
  }

  const ladder = data?.ladder;
  if (!ladder) {
    return (
      <>
        <PageTitle title="Your dose" />
        <PortalEmpty
          title="No dose to show yet"
          body="When your clinician prescribes a medicine with a dose you take yourself, your dose and any change to it appear here."
          action={
            <Link to="/portal" className="text-[13px] font-semibold hover:underline" style={{ color: TONE.forestText }}>
              Back to your dashboard
            </Link>
          }
        />
      </>
    );
  }

  const current = ladder.current;
  const waiting = ladder.notice ?? ladder.vialNotice;
  return (
    <div className="space-y-[8px] pb-1" data-testid="dose-page">
      <PageTitle title="Your dose" subtitle={`${ladder.itemName}, once a week`} />

      {waiting && <ChangeCard key={waiting.eventId} notice={waiting} onConfirmed={() => setConfirmed(true)} />}

      <Card className="px-[14px] pb-[13px] pt-[12px]">
        <CardHead label={waiting ? 'Your dose until you confirm' : 'Your dose now'} />
        <div className="mt-[8px]">
          {!ladder.active || !current ? (
            <Body>Your dose starts once you accept your plan. It appears here with the amount to draw for your vial.</Body>
          ) : (
            <CurrentDose current={current} />
          )}
        </div>
        {confirmed && !waiting && (
          <p className="mt-[8px] text-[12.5px] font-medium" style={{ color: TONE.forestText }} role="status">
            Confirmed
          </p>
        )}
      </Card>

      {ladder.stay && !waiting && (
        <Card className="px-[14px] py-[12px]" data-testid="dose-stay">
          <Headline>{ladder.stay.headline}</Headline>
          <Body className="mt-[5px]">{ladder.stay.body}</Body>
        </Card>
      )}

      <Card className="px-[14px] py-[12px]">
        <CardHead label="Your plan" />
        <Body className="mt-[7px]">{ladder.plan.body}</Body>
        <p className="mt-[8px] border-t pt-[8px] text-[13px] leading-relaxed" style={{ borderColor: TONE.line, color: TONE.heading }}>
          {ladder.sentence}
        </p>
      </Card>

      {ladder.history.length > 0 && (
        <Card className="px-[14px] py-[12px]">
          <CardHead label="Dose changes" />
          <HistoryList history={ladder.history} />
        </Card>
      )}

      <Link to="/portal/messages/new" className="flex h-[40px] items-center justify-center gap-2 rounded-[8px] bg-forest text-[13px] font-semibold text-white transition-colors hover:bg-forest-deep">
        Message your care team
        <ArrowRight size={15} strokeWidth={2} />
      </Link>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * On the plan page
 * ------------------------------------------------------------------ */

function useLadderFor(planId: string, itemName: string) {
  const { data } = useDoseSteps();
  const ladder = data?.ladder;
  return ladder && ladder.planId === planId && ladder.itemName === itemName ? ladder : null;
}

/** "Starting dose" for the microdose item's signed dose, which the dose steps may change; "Dose" for any other item. */
export function MicrodoseDoseLabel({ planId, itemName }: { planId: string; itemName: string }) {
  return <>{useLadderFor(planId, itemName) ? 'Starting dose' : 'Dose'}</>;
}

function Section({ label, children, testId }: { label?: string; children: ReactNode; testId?: string }) {
  return (
    <div className="mt-[10px] border-t pt-[10px]" style={{ borderColor: TONE.line }} data-testid={testId}>
      {label && (
        <p className="mb-[6px] text-[11px] font-semibold uppercase leading-none tracking-[0.08em]" style={{ color: TONE.faint }}>
          {label}
        </p>
      )}
      {children}
    </div>
  );
}

/**
 * Under the microdose item of the signed plan: the plan text with the off-label line (WHY-TIRZ-01 / WHY-SEMA-01), the
 * dose on the schedule now with the units for the vial, the range the ladder allows (OPT-STEP-001), and the changes
 * so far. Nothing for any other item or plan.
 */
export function MicrodoseRangeNote({ planId, itemName }: { planId: string; itemName: string }) {
  const ladder = useLadderFor(planId, itemName);
  if (!ladder) return null;
  const current = ladder.active ? ladder.current : null;
  const waiting = ladder.notice ?? ladder.vialNotice;
  return (
    <div data-testid="microdose-plan-note">
      <Section testId="microdose-plan-text">
        <p className="text-[13px] leading-[1.5]" style={{ color: TONE.heading }}>
          {ladder.plan.body}
        </p>
      </Section>
      {current && (
        <Section label="Your dose now" testId="microdose-dose-now">
          <p className="text-[14px] font-semibold" style={{ color: TONE.heading }}>
            {current.mgText} mg once a week
            <span className="ml-[6px] text-[12px] font-normal" style={{ color: TONE.faint }}>
              {sinceText(current.since)}
            </span>
          </p>
          {current.dose ? (
            <div className="mt-[8px]">
              <DoseRow dose={current.dose} />
            </div>
          ) : (
            <p className="mt-[4px] text-[12.5px]" style={{ color: TONE.body }}>
              The amount to draw appears here once your vial is recorded.
            </p>
          )}
          {waiting && (
            <p className="mt-[8px] flex flex-wrap items-center gap-x-[6px] text-[12.5px]" style={{ color: TONE.body }}>
              {waiting.kind === 'vial_change' ? 'Your new vial is waiting for you to confirm how much to draw.' : 'A change to your dose is waiting for you to confirm.'}
              <Link to="/portal" className="font-semibold hover:underline" style={{ color: TONE.forestText }}>
                Open your dashboard
              </Link>
            </p>
          )}
        </Section>
      )}
      <Section testId="microdose-range">
        <p className="text-[13px] leading-[1.45]" style={{ color: TONE.heading }}>
          {ladder.sentence}
        </p>
      </Section>
      {ladder.history.length > 0 && (
        <Section label="Dose changes">
          <HistoryList history={ladder.history} />
        </Section>
      )}
    </div>
  );
}
